import UserNotifications from "pages/userNotifications";
import { NOTIFICATION_CATEGORIES } from "constant/variables";
import React, { useState, useEffect } from "react";
import { Breadcrumb } from "components";
import { translate } from "react-switch-lang";
import { useHistory, useLocation, Link } from "react-router-dom";
import api from "components/api/api";
import axios from "axios";
import toast from "react-hot-toast";
import ImportModal from "./ImportModal";
import PreviewModal from "pages/PreviewModal/PreviewModal";
import UserSelector from "pages/Userselector/Userselector";
import GroupTargetSelector from "pages/createQuiz/GroupTargetSelector";

const CreateSurvey = ({ t }) => {
  const history = useHistory();
  const location = useLocation();
  const [regionData, setregionData] = useState({});
  const [regionNames, setRegionNames] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [mandatoryStatus, setMandatoryStatus] = useState("Optional");
  const [quizDuration, setQuizDuration] = useState({ hours: 0, minutes: 0 });
  const [quizTotalDuration, setQuizTotalDuration] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
  });
  const [showPreview, setShowPreview] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [maxRetake, setMaxRetake] = useState(1);
  const [visibilityType, setVisibilityType] = useState("public");
  const [userDataDisplayFields, setUserDataDisplayFields] = useState([
    "username",
    "region",
    "outlet",
    "staffId",
  ]);
  const [announcementMode, setAnnouncementMode] = useState("IMMEDIATE");
  const [scheduledTime, setScheduledTime] = useState("");

  // ── Targeting tabs ─────────────────────────────────────────────────────────
  const [targetTab, setTargetTab] = useState("region");
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  // Flat list of user IDs resolved from all checked groups
  const [selectedGroupUsers, setSelectedGroupUsers] = useState([]);

  // ── Fetch users & build regionData ────────────────────────────────────────

  const fetchUsers = async () => {
    try {
      const res = await api.fetchUsers();
      const users = res.data?.data || [];
      const filteredUsers = users;
      // const filteredUsers = users.filter((user) => user.role !== "ADMIN");
      const regionData = { "All Regions": {} };

      filteredUsers.forEach((user) => {
        const { id, username, region, outlet, staffId } = user;
        if (!region || !outlet || !username) return;
        if (!regionData[region]) regionData[region] = {};
        if (!regionData[region][outlet]) regionData[region][outlet] = [];
        regionData[region][outlet].push({ id, username, staffId });
      });
      return { regionData };
    } catch (error) {
      console.error("Error fetching users", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchAndSet = async () => {
      const data = await fetchUsers();
      if (data) {
        setregionData(data.regionData);
        setRegionNames(Object.keys(data.regionData));
      }
    };
    fetchAndSet();
  }, []);

  // Seed selectedUsers once regionData loads
  useEffect(() => {
    if (Object.keys(regionData).length === 0) return;
    const allUsers = Object.values(regionData)
      .flatMap((region) => Object.values(region || {}))
      .flat();
    setSelectedUsers(allUsers.map((u) => u.id));
  }, [regionData]);

  // ── Duplicate support ─────────────────────────────────────────────────────

  useEffect(() => {
    const dup = location.state?.duplicateData;
    if (!dup) return;

    setSurveyTitle(`${dup.title} (Copy)`);

    const pages = dup.json?.pages || [];
    const rebuilt = pages
      .flatMap((page) => page.elements || [])
      .map((el, i) => ({
        id: Date.now() + i,
        type: el.type || "radiogroup",
        text: el.title || "Question?",
        arabicText: el.arabicTitle || "",
        options: el.choices || [],
      }));

    if (rebuilt.length > 0) {
      setQuestions(rebuilt);
      setSelectedQuestionIndex(0);
    }

    if (dup.minutes) {
      const totalMins = dup.minutes;
      setQuizTotalDuration({
        days: Math.floor(totalMins / 1440),
        hours: Math.floor((totalMins % 1440) / 60),
        minutes: totalMins % 60,
      });
    }

    if (dup.visibilityType) setVisibilityType(dup.visibilityType.toLowerCase());
    if (dup.userDataDisplayFields?.length)
      setUserDataDisplayFields(dup.userDataDisplayFields);
  }, [location.state]);

  // ── Questions state ───────────────────────────────────────────────────────

  const [surveyTitle, setSurveyTitle] = useState("Untitled Survey");
  const [questions, setQuestions] = useState([
    {
      id: 1,
      type: "radiogroup",
      text: "Favorite Food?",
      arabicText: "",
      options: ["Cheese Mac Nuggets", "Burger", "KFC"],
      marks: 1,
    },
  ]);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);

  const handleAddQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      type: "radiogroup",
      text: "New Question?",
      arabicText: "",
      options: [],
    };
    setQuestions([...questions, newQuestion]);
    setSelectedQuestionIndex(questions.length);
  };

  const handleDeleteQuestion = () => {
    if (!questions || questions.length === 0) return;
    const updated = questions.filter(
      (_, index) => index !== selectedQuestionIndex,
    );
    setQuestions(updated);
    if (updated.length === 0) {
      setSelectedQuestionIndex(-1);
    } else if (selectedQuestionIndex >= updated.length) {
      setSelectedQuestionIndex(updated.length - 1);
    }
  };

  const handleAddOption = () => {
    const updated = [...questions];
    updated[selectedQuestionIndex].options.push("New Option");
    setQuestions(updated);
  };

  const handleDeleteOption = (optIndex) => {
    const updated = [...questions];
    updated[selectedQuestionIndex].options.splice(optIndex, 1);
    setQuestions(updated);
  };

  const handleOptionChange = (value, optIndex) => {
    const updated = [...questions];
    updated[selectedQuestionIndex].options[optIndex] = value;
    setQuestions(updated);
  };

  const handleTitleChange = (value) => {
    const updated = [...questions];
    updated[selectedQuestionIndex].text = value;
    setQuestions(updated);
  };

  const handleArabicTitleChange = (value) => {
    const updated = [...questions];
    updated[selectedQuestionIndex].arabicText = value;
    setQuestions(updated);
  };

  const handleTypeChange = (value) => {
    const updated = [...questions];
    updated[selectedQuestionIndex].type = value;
    if (["text", "comment", "rating", "boolean"].includes(value)) {
      updated[selectedQuestionIndex].options = [];
    }
    setQuestions(updated);
  };

  const handleSurveyTitleChange = (e) => setSurveyTitle(e.target.value);
  const handleMandatoryChange = (e) => setMandatoryStatus(e.target.value);

  // ── Preview / Import ──────────────────────────────────────────────────────

  const handlePreview = () => {
    if (questions.length === 0) {
      toast.error("Please add at least one question to preview the Survey.");
      return;
    }
    setShowPreview(true);
  };

  const handleImport = async (formData) => {
    try {
      const res = await api.importQuestions(formData);
      setQuestions(res.data.questions || []);
    } catch (error) {
      toast.error("Import failed!");
      throw error;
    }
  };

  // ── Publish ───────────────────────────────────────────────────────────────

  const transformToSurveyModel = () => {
    const elements = questions.map((q, index) => ({
      type: q.type,
      name: `q${index + 1}`,
      title: q.text,
      arabicTitle: q.arabicText,
      choices: ["radiogroup", "checkbox", "dropdown"].includes(q.type)
        ? q.options.filter((o) => o.trim())
        : undefined,
      marks: q.marks || 0,
    }));

    const totalquizMinutes =
      parseInt(quizTotalDuration.days || 0) * 1440 +
      parseInt(quizTotalDuration.hours || 0) * 60 +
      parseInt(quizTotalDuration.minutes || 0);

    const quizMinutes =
      parseInt(quizDuration.hours || 0) * 60 +
      parseInt(quizDuration.minutes || 0);

    return {
      type: "Survey",
      title: surveyTitle,
      definitionJson: { pages: [{ elements }] },
      status: true,
      isMandatory: mandatoryStatus === "Mandatory",
      quizTotalDuration: totalquizMinutes,
      quizDuration: quizMinutes,
      answerKey: null,
      maxScore: null,
      // Groups are frontend-only; backend always receives flat user IDs
      targetedUsers:
        targetTab === "region" ? selectedUsers : selectedGroupUsers,
      maxRetake,
      visibilityType,
      userDataDisplayFields,
      announcementMode,
      scheduledTime,
    };
  };

  const handlePublish = async () => {
    // Check for duplicate options in any question
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (["radiogroup", "checkbox", "dropdown"].includes(q.type)) {
        const trimmed = q.options.map((o) => o.trim().toLowerCase());
        const unique = new Set(trimmed);
        if (unique.size !== trimmed.length) {
          toast.error(
            `Question ${i + 1} has duplicate options. Please fix before publishing.`,
          );
          setSelectedQuestionIndex(i);
          return;
        }
      }
    }

    const surveyModel = transformToSurveyModel();
    try {
      await api.publishQuiz(surveyModel);
      toast.success("Survey published successfully!");
      history.push("/quiz-survey");
    } catch (error) {
      toast.error("Failed to publish survey.");
    }
  };

  const handleScheduledTimeChange = (e) => {
    const date = new Date(e.target.value);
    setScheduledTime(date.toISOString());
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrumb title={t("Create Survey")} />
      <div className="app-wrapper">
        <div className="animated slideInUpTiny animation-duration-3">
          <div className="row dashboardDetails">
            <div
              className="col-md-12 col-sm-12 col-xs-12"
              style={{ minHeight: "100vh", height: "auto" }}
            >
              <div className="create-survey">
                {/* ── Sidebar ── */}
                <aside className="sidebar card">
                  <input
                    className="survey-title-input"
                    value={surveyTitle}
                    onChange={handleSurveyTitleChange}
                    placeholder="Survey Title"
                  />
                  <button
                    onClick={handleAddQuestion}
                    className="new-question-btn"
                  >
                    NEW QUESTION <span className="plus">+</span>
                  </button>
                  <div className="question-list">
                    {questions.map((q, i) => (
                      <div
                        key={q.id}
                        className={`question-title ${i === selectedQuestionIndex ? "active" : ""}`}
                        onClick={() => setSelectedQuestionIndex(i)}
                      >
                        {q.text}
                      </div>
                    ))}
                  </div>
                </aside>

                {/* ── Main Editor ── */}
                {selectedQuestionIndex !== -1 && questions.length > 0 ? (
                  <main className="question-editor">
                    {/* ── Targeting Card ── */}
                    <div className="region-user-card card">
                      <div className="target-tabs">
                        <button
                          className={`target-tab-btn ${targetTab === "region" ? "active" : ""}`}
                          onClick={() => setTargetTab("region")}
                        >
                          Target User by Region &amp; Outlet
                        </button>
                        <button
                          className={`target-tab-btn ${targetTab === "group" ? "active" : ""}`}
                          onClick={() => setTargetTab("group")}
                        >
                          Target User by Groups
                        </button>
                      </div>

                      <div className="target-tab-content">
                        {targetTab === "region" && (
                          <div className="region-user-selectors">
                            <UserSelector
                              regionData={regionData}
                              selectedUsers={selectedUsers}
                              onChange={setSelectedUsers}
                            />
                          </div>
                        )}
                        {targetTab === "group" && (
                          <GroupTargetSelector
                            selectedGroupIds={selectedGroupIds}
                            onChange={setSelectedGroupIds}
                            // Receives the flat resolved user-ID list from all checked groups
                            onUsersChange={setSelectedGroupUsers}
                          />
                        )}
                      </div>

                      {/* ── Settings (always visible) ── */}
                      <div className="region-user-selectors">
                        <div className="mandatory-dropdown">
                          <label>Survey Mode:</label>
                          <select
                            value={mandatoryStatus}
                            onChange={handleMandatoryChange}
                          >
                            <option value="Optional">Optional</option>
                            <option value="Mandatory">Mandatory</option>
                          </select>

                          <div className="time-cont">
                            <div className="duration-input">
                              <label>⏰ Survey Duration (HH:MM):</label>
                              <div>
                                <input
                                  type="number"
                                  min="0"
                                  max="23"
                                  value={quizDuration.hours}
                                  onChange={(e) =>
                                    setQuizDuration({
                                      ...quizDuration,
                                      hours: Number(e.target.value) || 0,
                                    })
                                  }
                                  className="inputStyle"
                                />
                                <input
                                  type="number"
                                  min="0"
                                  max="59"
                                  value={quizDuration.minutes}
                                  onChange={(e) =>
                                    setQuizDuration({
                                      ...quizDuration,
                                      minutes: Number(e.target.value) || 0,
                                    })
                                  }
                                  className="inputStyle"
                                />
                              </div>
                            </div>

                            <div className="duration-input">
                              <label>⏰ Validity (Days:HH:MM):</label>
                              <div>
                                <input
                                  type="number"
                                  min="0"
                                  value={quizTotalDuration.days}
                                  onChange={(e) =>
                                    setQuizTotalDuration({
                                      ...quizTotalDuration,
                                      days: Number(e.target.value) || 0,
                                    })
                                  }
                                  className="inputStyle"
                                />
                                <span>:</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="23"
                                  value={quizTotalDuration.hours}
                                  onChange={(e) =>
                                    setQuizTotalDuration({
                                      ...quizTotalDuration,
                                      hours: Number(e.target.value) || 0,
                                    })
                                  }
                                  className="inputStyle"
                                />
                                <span>:</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="59"
                                  value={quizTotalDuration.minutes}
                                  onChange={(e) =>
                                    setQuizTotalDuration({
                                      ...quizTotalDuration,
                                      minutes: Number(e.target.value) || 0,
                                    })
                                  }
                                  className="inputStyle"
                                />
                              </div>
                            </div>

                            {/* <div className="duration-input">
                              <label>Max Re-attempt:</label>
                              <div>
                                <input
                                  type="number"
                                  min="0"
                                  max="5"
                                  value={maxRetake}
                                  onChange={(e) =>
                                    setMaxRetake(Number(e.target.value) || 0)
                                  }
                                  className="inputStyle"
                                />
                              </div>
                            </div> */}
                          </div>

                          <div className="duration-input">
                            <label>Visibility Type:</label>
                            <select
                              value={visibilityType}
                              onChange={(e) =>
                                setVisibilityType(e.target.value)
                              }
                            >
                              <option value="public">Public</option>
                              <option value="private">Private</option>
                            </select>
                          </div>

                          <div className="duration-input">
                            <label>User Data Fields:</label>
                            <div className="user-data-fields">
                              {["username", "region", "outlet", "staffId"].map(
                                (field) => (
                                  <label key={field}>
                                    <input
                                      type="checkbox"
                                      value={field}
                                      checked={userDataDisplayFields.includes(
                                        field,
                                      )}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setUserDataDisplayFields((prev) =>
                                          checked
                                            ? [...prev, field]
                                            : prev.filter((f) => f !== field),
                                        );
                                      }}
                                    />
                                    {field}
                                  </label>
                                ),
                              )}
                            </div>
                          </div>

                          <div className="duration-input">
                            <label>Announcement Mode:</label>
                            <select
                              value={announcementMode}
                              onChange={(e) =>
                                setAnnouncementMode(e.target.value)
                              }
                            >
                              <option value="IMMEDIATE">Immediate</option>
                              <option value="SCHEDULED">Scheduled</option>
                            </select>
                          </div>

                          {announcementMode === "SCHEDULED" && (
                            <div className="duration-input">
                              <label>Scheduled Time:</label>
                              <input
                                type="datetime-local"
                                value={
                                  scheduledTime
                                    ? scheduledTime.slice(0, 16)
                                    : ""
                                }
                                onChange={handleScheduledTimeChange}
                                className="inputStyleTime"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Question Card ── */}
                    {questions.length > 0 && (
                      <div className="question-card card">
                        <div className="editor-header">
                          <input
                            className="question-title-input"
                            type="text"
                            value={questions[selectedQuestionIndex].text}
                            onChange={(e) => handleTitleChange(e.target.value)}
                          />
                          <input
                            className="question-title-input"
                            type="text"
                            placeholder="أدخل السؤال بالعربية"
                            value={questions[selectedQuestionIndex].arabicText}
                            onChange={(e) =>
                              handleArabicTitleChange(e.target.value)
                            }
                            dir="rtl"
                          />
                          <select
                            className="question-type-select"
                            value={questions[selectedQuestionIndex].type}
                            onChange={(e) => handleTypeChange(e.target.value)}
                          >
                            <option value="radiogroup">Radio</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="text">Text</option>
                            <option value="comment">Comment</option>
                            <option value="dropdown">Dropdown</option>
                            <option value="rating">Rating</option>
                            <option value="boolean">Boolean</option>
                          </select>
                          <button
                            onClick={handleDeleteQuestion}
                            className="delete-question-btn"
                          >
                            ❌ DELETE QUESTION
                          </button>
                        </div>

                        <div className="options-list">
                          {["radiogroup", "checkbox", "dropdown"].includes(
                            questions[selectedQuestionIndex].type,
                          ) &&
                            questions[selectedQuestionIndex].options.map(
                              (opt, i) => (
                                <div className="option-row" key={i}>
                                  <span className="drag-icon">☰</span>
                                  <input
                                    type="text"
                                    value={opt}
                                    onChange={(e) =>
                                      handleOptionChange(e.target.value, i)
                                    }
                                  />
                                  <button
                                    className="delete-opt-btn"
                                    onClick={() => handleDeleteOption(i)}
                                  >
                                    ❌
                                  </button>
                                </div>
                              ),
                            )}

                          {["radiogroup", "checkbox", "dropdown"].includes(
                            questions[selectedQuestionIndex].type,
                          ) && (
                            <button
                              onClick={handleAddOption}
                              className="add-option-btn"
                            >
                              ADD <span className="plus">+</span>
                            </button>
                          )}

                          {questions[selectedQuestionIndex].type === "text" && (
                            <div className="review-preview">
                              <label>Preview:</label>
                              <input
                                type="text"
                                className="text-input-preview"
                                placeholder="Your answer here..."
                                disabled
                              />
                            </div>
                          )}

                          {questions[selectedQuestionIndex].type ===
                            "comment" && (
                            <div className="review-preview">
                              <label>Preview:</label>
                              <textarea
                                className="comment-preview"
                                placeholder="Write your comments here..."
                                disabled
                              />
                            </div>
                          )}

                          {questions[selectedQuestionIndex].type ===
                            "rating" && (
                            <div className="review-preview">
                              <label>Preview:</label>
                              <div className="star-rating-preview">
                                {[...Array(5)].map((_, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      color: "#FFD700",
                                      fontSize: "24px",
                                    }}
                                  >
                                    ★
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {questions[selectedQuestionIndex].type ===
                            "boolean" && (
                            <div className="review-preview">
                              <label>Preview:</label>
                              <div className="boolean-preview">
                                <label>
                                  <input type="radio" disabled /> Yes
                                </label>
                                <label style={{ marginLeft: "10px" }}>
                                  <input type="radio" disabled /> No
                                </label>
                              </div>
                            </div>
                          )}

                          {questions[selectedQuestionIndex].type === "html" && (
                            <div className="review-preview">
                              <label>Preview:</label>
                              <div className="html-preview">
                                <em>
                                  This is a static message for users. No
                                  response required.
                                </em>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="action-buttons">
                          <div className="saveBtnContainer">
                            <button
                              className="preview-btn"
                              onClick={handlePreview}
                              style={{
                                backgroundColor: "#6B7280",
                                color: "white",
                                border: "none",
                                padding: "10px 20px",
                                borderRadius: "5px",
                                cursor: "pointer",
                                marginRight: "10px",
                              }}
                            >
                              Preview Survey
                            </button>
                            <button
                              onClick={handlePublish}
                              className="publish-btn"
                            >
                              Publish Survey
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </main>
                ) : (
                  <div className="no-questions-message">
                    No questions available. Please add a question.
                  </div>
                )}

                <ImportModal
                  show={showImportModal}
                  onClose={() => setShowImportModal(false)}
                  onImport={handleImport}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <PreviewModal
        show={showPreview}
        onClose={() => setShowPreview(false)}
        quizTitle={surveyTitle}
        questions={questions}
        quizType="Survey"
      />
    </>
  );
};

export default translate(CreateSurvey);
